import React, { useState, useMemo } from 'react';
import { Plus, Copy, Mail, AlertCircle, CheckCircle, Trash2, Search, User, Send } from 'lucide-react';
import { judgeInvitationService, emailService } from '../../services/supabaseService';
import './JudgeInvitationManager.css';
import logger from '../../utils/logger';

const JudgeInvitationManager = ({ events = [], judges = [], auth = null }) => {
  const [step, setStep] = useState('list'); // 'list' | 'generate'
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedCode, setCopiedCode] = useState(null);

  // Formulario para generar invitación
  const [formData, setFormData] = useState({
    eventId: '',
    selectedJudgeId: '',
    judgeEmail: '',
    judge_name: '',
    manualEntry: false
  });

  // Jueces activos para el combo
  const activeJudges = useMemo(() => {
    return (judges || []).filter(j => j.is_active !== false);
  }, [judges]);

  // Al seleccionar un juez del combo
  const handleJudgeSelect = (judgeId) => {
    if (judgeId === 'manual') {
      setFormData({
        ...formData,
        selectedJudgeId: '',
        judgeEmail: '',
        judge_name: '',
        manualEntry: true
      });
      return;
    }

    const judge = activeJudges.find(j => j.id === judgeId);
    if (judge) {
      setFormData({
        ...formData,
        selectedJudgeId: judge.id,
        judgeEmail: judge.email || '',
        judge_name: judge.name || '',
        manualEntry: false
      });
    } else {
      setFormData({
        ...formData,
        selectedJudgeId: '',
        judgeEmail: '',
        judge_name: '',
        manualEntry: false
      });
    }
  };

  // Generar nueva invitación
  const handleGenerateInvitation = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!formData.eventId) {
      setError('Por favor selecciona un evento');
      setLoading(false);
      return;
    }

    if (!formData.judge_name.trim()) {
      setError('Por favor selecciona un juez o ingresa el nombre');
      setLoading(false);
      return;
    }

    if (!formData.judgeEmail.trim()) {
      setError('Por favor ingresa el correo del juez');
      setLoading(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.judgeEmail)) {
      setError('Por favor ingresa un correo válido');
      setLoading(false);
      return;
    }

    try {
      const adminId = auth?.id || auth?.user?.id;
      
      if (!adminId) {
        setError('Error: Usuario no autenticado');
        setLoading(false);
        return;
      }

      const result = await judgeInvitationService.generateInvitation(
        adminId,
        formData.eventId
      );

      if (result.error) {
        setError(typeof result.error === 'string' ? result.error : 'Error al generar invitación');
        setLoading(false);
        return;
      }

      const event = events.find(e => e.id === formData.eventId);
      const eventName = event ? event.name : 'Evento';

      const newInvitation = {
        ...result.data,
        judge_email: formData.judgeEmail,
        judge_name: formData.judge_name,
        event_name: eventName,
        email_sent: false
      };

      // Intentar enviar correo automáticamente
      const emailResult = await emailService.sendInvitationCode(
        formData.judgeEmail,
        formData.judge_name,
        result.data.code,
        eventName
      );

      if (emailResult.error) {
        logger.error('⚠️ Error enviando correo:', emailResult.error);
        newInvitation.email_sent = false;
        setSuccess(`✅ Código generado: ${result.data.code}\n⚠️ No se pudo enviar el correo automáticamente. Puedes copiarlo y enviarlo manualmente.`);
      } else {
        newInvitation.email_sent = true;
        setSuccess(`✅ Código generado y correo enviado a ${formData.judgeEmail}`);
      }

      setInvitations([newInvitation, ...invitations]);

      // Reset form
      setFormData({
        eventId: '',
        selectedJudgeId: '',
        judgeEmail: '',
        judge_name: '',
        manualEntry: false
      });

      // Volver a lista después de 3 segundos
      setTimeout(() => {
        setStep('list');
        setSuccess('');
      }, 3000);
    } catch (err) {
      logger.error('Error:', err?.message);
      setError('Error al generar invitación');
    } finally {
      setLoading(false);
    }
  };

  // Copiar código
  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Reenviar correo
  const handleResendEmail = async (invitation) => {
    setSendingEmail(invitation.id);
    setError('');
    
    try {
      const result = await emailService.sendInvitationCode(
        invitation.judge_email,
        invitation.judge_name,
        invitation.code,
        invitation.event_name
      );

      if (result.error) {
        setError(`❌ Error al reenviar: ${typeof result.error === 'string' ? result.error : 'Intenta más tarde'}`);
      } else {
        // Marcar como enviado
        setInvitations(prev => prev.map(inv => 
          inv.id === invitation.id ? { ...inv, email_sent: true } : inv
        ));
        setSuccess(`✅ Correo reenviado a ${invitation.judge_email}`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      logger.error('Error reenviando correo:', err?.message);
      setError('Error al reenviar correo');
    } finally {
      setSendingEmail(null);
    }
  };

  // Eliminar invitación de la lista local
  const handleDeleteInvitation = (invitationId) => {
    setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
  };

  // Vista: Lista de Invitaciones
  if (step === 'list') {
    return (
      <div className="invitation-manager">
        <div className="invitation-header">
        
          <button
            onClick={() => { setStep('generate'); setError(''); setSuccess(''); }}
            className="btn btn-primary"
          >
            <Plus size={16} />
            Nueva Invitación
          </button>
        </div>

        {error && (
          <div className="alert error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert success">
            <CheckCircle size={20} />
            <span>{success}</span>
          </div>
        )}

        {invitations.length === 0 ? (
          <div className="empty-state">
            <Mail size={48} color="#d1d5db" />
            <p>No hay invitaciones generadas aún</p>
            <button
              onClick={() => setStep('generate')}
              className="btn btn-primary"
            >
              Crear primera invitación
            </button>
          </div>
        ) : (
          <div className="invitations-list">
            {invitations.map(invitation => (
              <div key={invitation.id} className="invitation-card">
                <div className="invitation-content">
                  <div className="invitation-info">
                    <h4>{invitation.judge_name}</h4>
                    <p className="email">{invitation.judge_email}</p>
                    <p className="event">📅 {invitation.event_name}</p>
                  </div>

                  <div className="invitation-code">
                    <code>{invitation.code}</code>
                    <button
                      onClick={() => handleCopyCode(invitation.code)}
                      className="copy-btn"
                      title="Copiar código"
                    >
                      {copiedCode === invitation.code ? (
                        <CheckCircle size={18} color="#10b981" />
                      ) : (
                        <Copy size={18} />
                      )}
                    </button>
                  </div>

                  <div className="invitation-status">
                    {invitation.used ? (
                      <span className="status used">✓ Utilizado</span>
                    ) : new Date(invitation.expires_at) < new Date() ? (
                      <span className="status expired">✗ Expirado</span>
                    ) : (
                      <span className="status pending">⏱ Pendiente</span>
                    )}
                    {invitation.email_sent && (
                      <span className="status email-sent">📧 Enviado</span>
                    )}
                  </div>
                </div>

                <div className="invitation-actions">
                  <button
                    className="btn-action send"
                    title="Reenviar por email"
                    onClick={() => handleResendEmail(invitation)}
                    disabled={sendingEmail === invitation.id || invitation.used}
                  >
                    {sendingEmail === invitation.id ? (
                      <span className="spinner-small"></span>
                    ) : (
                      <Send size={18} />
                    )}
                  </button>
                  <button
                    className="btn-action delete"
                    title="Eliminar"
                    onClick={() => handleDeleteInvitation(invitation.id)}
                    disabled={sendingEmail === invitation.id}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Vista: Generar Invitación
  return (
    <div className="invitation-manager">
      <div className="invitation-header">
        <h3>📨 Generar Código de Invitación</h3>
        <button
          onClick={() => { setStep('list'); setError(''); setSuccess(''); }}
          className="btn btn-secondary"
        >
          ← Volver
        </button>
      </div>

      <form onSubmit={handleGenerateInvitation} className="invitation-form">
        {error && (
          <div className="alert error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert success">
            <CheckCircle size={20} />
            <span style={{ whiteSpace: 'pre-line' }}>{success}</span>
          </div>
        )}

        {/* Selector de Evento */}
        <div className="form-group">
          <label htmlFor="eventId">📅 Evento *</label>
          <select
            id="eventId"
            value={formData.eventId}
            onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
            disabled={loading}
          >
            <option value="">Selecciona un evento</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.name} — {event.date}
              </option>
            ))}
          </select>
        </div>

        {/* Selector de Juez Registrado */}
        <div className="form-group">
          <label htmlFor="judgeSelect">
            <User size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
            Juez Registrado *
          </label>
          <select
            id="judgeSelect"
            value={formData.manualEntry ? 'manual' : formData.selectedJudgeId}
            onChange={(e) => handleJudgeSelect(e.target.value)}
            disabled={loading}
          >
            <option value="">Selecciona un juez</option>
            {activeJudges.map(judge => (
              <option key={judge.id} value={judge.id}>
                {judge.name} {judge.email ? `(${judge.email})` : ''}
              </option>
            ))}
            <option value="manual" style={{ borderTop: '1px solid #ccc' }}>
              ✏️ Ingresar datos manualmente...
            </option>
          </select>
          {activeJudges.length === 0 && (
            <p className="form-hint warning">
              ⚠️ No hay jueces registrados. Puedes ingresar los datos manualmente.
            </p>
          )}
        </div>

        {/* Campos de Nombre y Email (se auto-llenan al seleccionar juez) */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="judge_name">Nombre del Juez *</label>
            <input
              id="judge_name"
              type="text"
              value={formData.judge_name}
              onChange={(e) => setFormData({ ...formData, judge_name: e.target.value })}
              placeholder="Ej: Juan Pérez"
              disabled={loading || (!formData.manualEntry && formData.selectedJudgeId)}
              className={!formData.manualEntry && formData.selectedJudgeId ? 'auto-filled' : ''}
            />
          </div>

          <div className="form-group">
            <label htmlFor="judgeEmail">Correo del Juez *</label>
            <input
              id="judgeEmail"
              type="email"
              value={formData.judgeEmail}
              onChange={(e) => setFormData({ ...formData, judgeEmail: e.target.value })}
              placeholder="juez@ejemplo.com"
              disabled={loading || (!formData.manualEntry && formData.selectedJudgeId)}
              className={!formData.manualEntry && formData.selectedJudgeId ? 'auto-filled' : ''}
            />
          </div>
        </div>

        <div className="form-info">
          <p>
            <strong>📧 Envío automático:</strong> Al generar el código, se enviará automáticamente un correo al juez con el código de invitación y los datos del evento.
          </p>
          <p style={{ marginTop: 8 }}>
            <strong>⏱ Validez:</strong> El código es válido por 30 días.
          </p>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn btn-primary full-width"
        >
          {loading ? (
            <>
              <span className="spinner-small"></span>
              Generando y enviando...
            </>
          ) : (
            <>
              <Send size={18} />
              Generar y Enviar Invitación
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default JudgeInvitationManager;
